import { report } from '../_test/data/progress';
import {locales} from "Constants/constants";
import { generateProgressReport } from '../util/generators/generateProgressReport';
import { accountForTrendline } from '../state/progress/creators';
import {hasPendingAssessment} from '../util/pendingAssessment';
import {fetchARMScore} from '../util/getArmScore';
import { fetchScaledCutlines } from '../../../services/query/benchmarkReportAPI';
import { get } from 'lodash';
import store from '../../../store';
import { ScoreType } from '@/enums/ScoreType';
import getARMBoxes from './parent/getARMBoxes';
import { getProgressReportingActivities, getReportingActivitiesForStudent } from '@/services/reportingActivitiesService';

// Helper function to wait for Redux state condition
const waitForState = (selector, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    // Check if condition is already met
    if (selector(store.getState())) {
      return resolve(store.getState());
    }
    
    // Set up subscription
    const unsubscribe = store.subscribe(() => {
      if (selector(store.getState())) {
        unsubscribe();
        resolve(store.getState());
      } else if (Date.now() - startTime > timeout) {
        unsubscribe();
        reject(new Error('Timeout waiting for state condition'));
      }
    });
  });
};

// TODO generate report, add scale method (or reconsider activity caching)
export const getReport = async (student, metric, scale, equating, locale, additionalProps={}, scaledCutlines) => {
  try {
    let activities = await getReportingActivitiesForStudent(student.value, null, equating, locale) || [];

    // Filter activities for Student Summary based on score type
    if (additionalProps.pageType === 'studentSummary') {
      const state = store.getState();
      const scoreType = state.userEntitlement.data.scoreType;
      
      if (scoreType === ScoreType.ISIP) {
        // ISIP: only assessment activities
        activities = activities.filter(a => a.type === 'assessment');
      } else {
        // ARM: assessment + tutor activities  
        activities = activities.filter(a => a.type === 'assessment' || a.type === 'tutor');
      }
    }

    if (scale !== "ARM")
      activities = activities.filter(a => a.status !== "not_started");

    // generateProgressReport expects: (activities, metricId, scaleId, locale, pageType, equating)
    let report = await generateProgressReport(activities, metric, scale, locale, additionalProps.pageType, equating);
    
    // Check if report was generated successfully
    if (!report) {
      console.warn("❌ Progress Report: Failed to generate progress report. Returning empty report.");
      return [null, activities];
    }
    
    report = await accountForTrendline(report, scale, student, locale);

    const armScorePromise = additionalProps.skipARM ? Promise.resolve(null) : fetchARMScore(student, activities);
    const pendingAssessmentPromise = hasPendingAssessment(student.value);

    // Get ARM boxes using simplified approach
    let armBoxes = {};
    try {
      const state = store.getState();
      const benchmark = get(state, 'reports.teacher.selections.benchmark');
      const customBenchmarks = get(state, 'reports.teacher.customBenchmarks');
      const metricObj = { value: metric };
      
      armBoxes = await getARMBoxes(student, metricObj, benchmark, customBenchmarks, locale, equating);
    } catch (error) {
      console.warn("❌ Progress Report: Failed to calculate armBoxes:", error);
      armBoxes = {};
    }

    const [armScore, isPending] = await Promise.all([armScorePromise, pendingAssessmentPromise]);

    report.armScore = armScore;
    report.isPending = isPending;
    report.armBoxes = armBoxes;

    return [report, activities];
  } catch (error) {
    console.error("An error occurred while fetching the report:", error);
    throw error;
  }
};

const fetchProgressActivities = async (student, equating, locale, metric, scale, additionalProps) => {
  try {
    const activities = await fetchActivities(student, equating, locale, scale);
    if(!activities.student || !activities.student.grade){
      activities.map((act,index)=>activities[index].student=student);
    }

    // Get scaled cutlines from Redux state
    let scaledCutlines = null;
    const state = store.getState();
    const districtId = get(state, 'reports.roster.data.district[0].id');
    const schoolId = get(state, 'reports.roster.data.school[0].id');
    
    if (districtId && schoolId && student?.grade !== undefined) {
      try {
        // Wait for assessment calendar periods to be available
        await waitForState(state => {
          const calendar = state.calendar.assessmentCalendar;
          return calendar?.periods?.length > 0;
        });
        const assessmentCalendar = store.getState().calendar.assessmentCalendar;
        const periodIds = assessmentCalendar.periods.map(period => period.periodIdentifier);
        const params = {
          districtId,
          schoolId,
          grade: student.grade,
          scale: state.userEntitlement.data.scoreType || "ARM",
          periodId: periodIds
        };

        scaledCutlines = await fetchScaledCutlines(params);
        store.dispatch({
          type: 'PROGRESS_CUTLINES_COMPLETE',
          cutlines: scaledCutlines
        });
      } catch (error) {
        console.warn("Failed to get assessment calendar periods or fetch scaled cutlines:", error);
        return [null, activities];
      }
    } else {
      console.warn("Missing required data for cutlines:", { 
        hasDistrictId: !!districtId, 
        hasSchoolId: !!schoolId, 
        hasStudentGrade: student?.grade !== undefined 
      });
    }

    // Generate the report with the available cutlines
    let report = await generateProgressReport(activities, metric, scale, locale, additionalProps.pageType, equating);
    if (!report) {
      throw new Error('Failed to generate progress report');
    }

    report = await accountForTrendline(report, scale, student, locale);
    return [report, activities];
  } catch (error) {
    console.error("Error in fetchProgressActivities:", error);
    throw error;
  }
};

export default {
  getReport
}

